import { Modal } from '@/components/ui/Modal';
import { LineChart } from '@/components/dashboard/Charts';
import { EmptyChartState } from '@/components/dashboard/DashboardCards';
import {
  formatCount,
  formatMoney,
} from '@/components/dashboard/commandCenterConfig';
import type { CommissionPeriod, OrderStatistics } from '@/types/dashboard';

const PERIOD_LABELS: Record<string, string> = {
  this_year: 'This year',
  this_month: 'This month',
  this_week: 'This week',
  overall: 'Overall',
};

export function SalesDetailsModal({
  open,
  onClose,
  grossSale,
  commission,
  deliveryCommission,
  series,
  period,
  stats,
  loading,
}: {
  open: boolean;
  onClose: () => void;
  grossSale: number | null;
  commission: number | null;
  deliveryCommission: number | null;
  series: Array<{ label: string; value: number }>;
  period: CommissionPeriod;
  stats: OrderStatistics | undefined;
  loading: boolean;
}) {
  const hasData = series.length > 0 && series.some((p) => (p.value || 0) > 0);

  return (
    <Modal open={open} title="Sales details" onClose={onClose} wide>
      <div className="sales-modal">
        <div className="sales-modal-meta">
          <div>
            <p className="muted">Period</p>
            <p className="sales-modal-value">{PERIOD_LABELS[period] ?? period}</p>
          </div>
          <div>
            <p className="muted">Gross sales</p>
            <p className="sales-modal-value">
              {loading && grossSale == null ? (
                <span className="skeleton" style={{ height: 24, width: 100, display: 'inline-block' }} />
              ) : (
                formatMoney(grossSale)
              )}
            </p>
          </div>
          <div>
            <p className="muted">Commission</p>
            <p className="sales-modal-value">{formatMoney(commission)}</p>
          </div>
          <div>
            <p className="muted">Delivery commission</p>
            <p className="sales-modal-value">{formatMoney(deliveryCommission)}</p>
          </div>
        </div>

        <div className="sales-modal-breakdown">
          <h3>Order volume in period</h3>
          <ul className="sales-modal-list">
            <li>
              <span>Total orders</span>
              <strong>{formatCount(stats?.total_orders)}</strong>
            </li>
            <li>
              <span>Delivered</span>
              <strong>{formatCount(stats?.delivered)}</strong>
            </li>
            <li>
              <span>Canceled</span>
              <strong>{formatCount(stats?.canceled)}</strong>
            </li>
            <li>
              <span>Payment failed</span>
              <strong>{formatCount(stats?.payment_failed)}</strong>
            </li>
          </ul>
        </div>

        <div className="sales-modal-chart">
          <h3>Gross sales trend</h3>
          {!hasData ? (
            <EmptyChartState
              title="No sales data yet"
              description="Sales information will appear when orders are completed."
            />
          ) : (
            <LineChart
              bare
              title="Gross sales"
              points={series.map((s) => ({ label: s.label, value: s.value }))}
              color="#0d9488"
              format={(v) => formatMoney(v)}
            />
          )}
        </div>
      </div>
    </Modal>
  );
}
